import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { CheckoutTreeNode, CheckoutTreeNodeSchema } from "./checkout-tree";
import { FulfillmentCutoffScheduleParser } from "./fulfillment-cutoff";

export enum CheckoutOptimizationWidgetType {
  FULFILLMENT_CUTOFF = "FULFILLMENT_CUTOFF",
  THRESHOLD_UPSELL = "THRESHOLD_UPSELL",
}

export const checkoutOptimizationWidgetTypeToLabel: Record<
  CheckoutOptimizationWidgetType,
  string
> = {
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: "Fulfillment cutoff",
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: "Upsell",
};

// targets for purchase.checkout.block.render (as of Mar. 2025)
export enum PurchaseCheckoutBlockRenderTarget {
  INFORMATION1 = "INFORMATION1",
  INFORMATION2 = "INFORMATION2",
  DELIVERY1 = "DELIVERY1",
  PAYMENT1 = "PAYMENT1",
  PAYMENT2 = "PAYMENT2",
  ORDER_SUMMARY1 = "ORDER_SUMMARY1",
  ORDER_SUMMARY2 = "ORDER_SUMMARY2",
  ORDER_SUMMARY3 = "ORDER_SUMMARY3",
  ORDER_SUMMARY4 = "ORDER_SUMMARY4",
}

export const PurchaseCheckoutBlockRenderTargetToDisplayText: Record<
  PurchaseCheckoutBlockRenderTarget,
  string
> = {
  [PurchaseCheckoutBlockRenderTarget.INFORMATION1]: "Information 1",
  [PurchaseCheckoutBlockRenderTarget.INFORMATION2]: "Information 2",
  [PurchaseCheckoutBlockRenderTarget.DELIVERY1]: "Delivery 1",
  [PurchaseCheckoutBlockRenderTarget.PAYMENT1]: "Payment 1",
  [PurchaseCheckoutBlockRenderTarget.PAYMENT2]: "Payment 2",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1]: "Order summary 1",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY2]: "Order summary 2",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY3]: "Order summary 3",
  [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY4]: "Order summary 4",
};

export const FULFILLMENT_CUTOFF_WIDGET_ORDER_CUTOFF_TIME_PLACEHOLDER =
  "{{order_cutoff_time}}";
export const FULFILLMENT_CUTOFF_WIDGET_DAY_OF_ORDER_CUTOFF_TIME_PLACEHOLDER =
  "{{day_of_order_cutoff_time}}";
export const FULFILLMENT_CUTOFF_WIDGET_FASTEST_DELIVERY_DATE_PLACEHOLDER =
  "{{fastest_delivery_date}}";

const CheckoutOptimizationFulfillmentCutoffWidgetSchema = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
  _id: zExt.objectId(),
  text: z.string(),
  backgroundColor: z.string(),
  iconColor: z.string(),
});
export type CheckoutOptimizationFulfillmentCutoffWidget = z.infer<
  typeof CheckoutOptimizationFulfillmentCutoffWidgetSchema
>;

export const THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER =
  "{{amount_left}}";

export const THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT = "Add";

export enum ManualProductSourceType {
  COLLECTION = "COLLECTION",
  PRODUCT = "PRODUCT",
  TAGS = "TAGS",
}

const ManualProductSourceCollectionSchema_ = z.object({
  type: z.literal(ManualProductSourceType.COLLECTION),
  collectionId: z.string(),
  collectionTitle: z.string().optional(),
});
export const ManualProductSourceCollectionSchema: z.ZodType<ManualProductSourceCollection> =
  ManualProductSourceCollectionSchema_;
export interface ManualProductSourceCollection
  extends z.infer<typeof ManualProductSourceCollectionSchema_> {}

const ManualProductSourceProductSchema_ = z.object({
  type: z.literal(ManualProductSourceType.PRODUCT),
  productId: z.string(),
  productTitle: z.string().optional(),
  productImageUrl: z.string().optional(),
});
export const ManualProductSourceProductSchema: z.ZodType<ManualProductSourceProduct> =
  ManualProductSourceProductSchema_;
export interface ManualProductSourceProduct
  extends z.infer<typeof ManualProductSourceProductSchema_> {}

const ManualProductSourceTagsSchema_ = z.object({
  type: z.literal(ManualProductSourceType.TAGS),
  tags: z.array(z.string()),
});
export const ManualProductSourceTagsSchema: z.ZodType<ManualProductSourceTags> =
  ManualProductSourceTagsSchema_;
export interface ManualProductSourceTags
  extends z.infer<typeof ManualProductSourceTagsSchema_> {}

export const ManualProductSourceSchema_ = z.discriminatedUnion("type", [
  ManualProductSourceCollectionSchema_,
  ManualProductSourceProductSchema_,
  ManualProductSourceTagsSchema_,
]);
export const ManualProductSourceSchema: z.ZodType<ManualProductSource> =
  ManualProductSourceSchema_;
export type ManualProductSource = z.infer<typeof ManualProductSourceSchema_>;

const CheckoutOptimizationThresholdUpsellWidgetSchema = z.object({
  type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
  _id: zExt.objectId(),
  threshold: z.object({ amount: z.number(), currency: z.string() }),
  gteThresholdText: z.string(),
  ltThresholdText: z.string(),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  progressBarColor: z
    .object({ filled: z.string().optional(), unfilled: z.string().optional() })
    .optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(ManualProductSourceSchema_).optional(),
});
export type CheckoutOptimizationThresholdUpsellWidget = z.infer<
  typeof CheckoutOptimizationThresholdUpsellWidgetSchema
>;

const WidgetTargetConfigSchema = z.object({
  widgets: z.array(
    z.discriminatedUnion("type", [
      CheckoutOptimizationFulfillmentCutoffWidgetSchema,
      CheckoutOptimizationThresholdUpsellWidgetSchema,
    ]),
  ),
});
export type WidgetTargetConfigWidgets = z.infer<
  typeof WidgetTargetConfigSchema.shape.widgets
>;

export const CheckoutWidgetsConfigSchema = z.object({
  targetToConfig: z.object({
    [PurchaseCheckoutBlockRenderTarget.INFORMATION1]: WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.INFORMATION2]: WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.DELIVERY1]: WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.PAYMENT1]: WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.PAYMENT2]: WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1]:
      WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY2]:
      WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY3]:
      WidgetTargetConfigSchema,
    [PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY4]:
      WidgetTargetConfigSchema,
  } satisfies Record<
    PurchaseCheckoutBlockRenderTarget,
    typeof WidgetTargetConfigSchema
  >),
});
export type CheckoutWidgetsConfig = z.infer<typeof CheckoutWidgetsConfigSchema>;

export const WidgetCheckoutOptimizationConfigSchema = z.object({
  checkoutTreeNode: CheckoutTreeNodeSchema.nullable(),
  checkoutExperienceIdToWidgetConfig: z.record(
    z.string(),
    CheckoutWidgetsConfigSchema,
  ),
  cutoffTimeSchedule: FulfillmentCutoffScheduleParser,
  globalWidgetConfig: CheckoutWidgetsConfigSchema.nullable(),
  rateTableIdToCheckoutExperienceId: z.record(z.string(), z.string()),
});
export type WidgetCheckoutOptimizationConfig = z.infer<
  typeof WidgetCheckoutOptimizationConfigSchema
>;
export type ManuallyTypedWidgetCheckoutOptimizationConfig = Omit<
  WidgetCheckoutOptimizationConfig,
  "checkoutTreeNode"
> & { checkoutTreeNode: CheckoutTreeNode | null };
